import { z } from 'zod';
import { senhaSchema } from './auth';
import { cpfValido } from './mascara';

/** Fase 3 · CRM, Central de Atendimento e equipe (docs/06). */

export const ORIGENS_LEAD = [
  'site',
  'whatsapp',
  'instagram',
  'chat_site',
  'portal',
  'indicacao',
  'balcao',
  'telefone',
  'simulador',
  'reserva',
  'campanha',
  'importacao',
  'site_proprio',
  'automatico',
  'facebook',
  'evento',
] as const;
export type OrigemLead = (typeof ORIGENS_LEAD)[number];
/** Origens que a equipe pode escolher ao cadastrar um lead à mão. */
export const ORIGENS_MANUAIS = [
  'balcao',
  'indicacao',
  'telefone',
  'instagram',
  'whatsapp',
] as const;

export const NOME_ORIGEM: Record<string, string> = {
  site: 'Site',
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  chat_site: 'Chat do site',
  portal: 'Portal',
  indicacao: 'Indicação',
  balcao: 'Balcão',
  telefone: 'Telefone',
  simulador: 'Simulador',
  reserva: 'Reserva online',
  campanha: 'Campanha',
  importacao: 'Importação',
  site_proprio: 'Site próprio',
  automatico: 'Lembrete automático',
  avaliacao: 'Link de avaliação',
  facebook: 'Anúncio do Facebook',
  portal_olx: 'OLX',
  portal_webmotors: 'Webmotors',
  portal_icarros: 'iCarros',
  portal_mercadolivre: 'Mercado Livre',
  evento: 'Evento da loja',
};

/** Etapas iniciais do funil (a loja edita). */
export const FUNIL_PADRAO = [
  { nome: 'Novo', tipo: 'aberta' },
  { nome: 'Em contato', tipo: 'aberta' },
  { nome: 'Visita / test drive', tipo: 'aberta' },
  { nome: 'Proposta', tipo: 'aberta' },
  { nome: 'Vendido', tipo: 'ganho' },
  { nome: 'Perdido', tipo: 'perdido' },
] as const;
export const TIPOS_ETAPA = ['aberta', 'ganho', 'perdido'] as const;
export type TipoEtapa = (typeof TIPOS_ETAPA)[number];

/** Só dígitos; acrescenta 55 em números brasileiros com DDD (10 ou 11 dígitos). */
export function normalizarTelefone(t: string | null | undefined): string | null {
  const d = (t ?? '').replace(/\D/g, '');
  if (!d) return null;
  if (d.length === 10 || d.length === 11) return `55${d}`;
  return d;
}
export const telefoneValido = (t: string | null | undefined) =>
  /^\d{12,13}$/.test(normalizarTelefone(t) ?? '');

const texto = (max: number) => z.string().trim().max(max);
const textoOpc = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const centavos = z.number().int().min(0).max(100_000_000_00);

const telefoneCampo = z
  .string()
  .trim()
  .refine((t) => telefoneValido(t), 'Telefone com DDD')
  .transform((t) => normalizarTelefone(t)!);

export const clienteSalvarSchema = z
  .object({
    nome: texto(120).min(2, 'Informe o nome'),
    telefone: telefoneCampo.nullable().optional(),
    email: z.string().trim().toLowerCase().email('E-mail inválido').max(200).nullable().optional(),
    cpf: z
      .string()
      .transform((c) => c.replace(/\D/g, ''))
      .refine((c) => c === '' || cpfValido(c), 'CPF inválido')
      .transform((c) => (c === '' ? null : c))
      .nullable()
      .optional(),
    cidade: textoOpc(80),
    nascimento: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida')
      .nullable()
      .optional(),
    observacoes: textoOpc(2000),
    consentimento: z.boolean().default(false),
  })
  .refine((c) => c.telefone || c.email, {
    message: 'Informe telefone ou e-mail',
    path: ['telefone'],
  });
export type ClienteSalvar = z.infer<typeof clienteSalvarSchema>;

export const leadCriarSchema = z.object({
  clienteId: z.string().uuid().optional(),
  cliente: clienteSalvarSchema.optional(),
  origem: z.enum(ORIGENS_MANUAIS),
  veiculoId: z.string().uuid().nullable().optional(),
  filialId: z.string().uuid().optional(),
  mensagem: textoOpc(2000),
  vendedorId: z.string().uuid().optional(),
});

export const leadAtualizarSchema = z.object({
  etapaId: z.string().uuid().optional(),
  vendedorId: z.string().uuid().nullable().optional(),
  motivoPerda: textoOpc(300),
  veiculoId: z.string().uuid().nullable().optional(),
});

export const etapasSalvarSchema = z.object({
  etapas: z
    .array(
      z.object({
        id: z.string().uuid().optional(),
        nome: texto(40).min(2),
        tipo: z.enum(TIPOS_ETAPA),
      }),
    )
    .min(2)
    .max(12)
    .refine((e) => e.some((x) => x.tipo === 'ganho') && e.some((x) => x.tipo === 'perdido'), {
      message: 'O funil precisa de uma etapa de venda e uma de perda',
    }),
});

export const FORMAS = ['a_vista', 'financiado', 'misto'] as const;

export const propostaCriarSchema = z.object({
  veiculoId: z.string().uuid(),
  valorCentavos: centavos.refine((v) => v > 0, 'Informe o valor'),
  entradaCentavos: centavos.nullable().optional(),
  forma: z.enum(FORMAS),
  parcelas: z.number().int().min(1).max(96).nullable().optional(),
  trocaId: z.string().uuid().nullable().optional(),
  observacoes: textoOpc(1000),
  motivoDesconto: textoOpc(300),
});

export const avaliacaoCriarSchema = z.object({
  placa: z
    .string()
    .transform((p) => p.toUpperCase().replace(/[^A-Z0-9]/g, ''))
    .refine((p) => /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(p), 'Placa inválida'),
  marca: texto(60).min(2),
  modelo: texto(80).min(1),
  anoModelo: z.number().int().min(1950).max(2100),
  km: z.number().int().min(0).max(2_000_000),
  valorAvaliadoCentavos: centavos.refine((v) => v > 0, 'Informe o valor'),
  observacoes: textoOpc(1000),
});

export const testDriveCriarSchema = z.object({
  veiculoId: z.string().uuid(),
  inicio: z.coerce.date(),
  vendedorId: z.string().uuid().optional(),
  observacoes: textoOpc(500),
});
export const STATUS_TEST_DRIVE = ['agendado', 'realizado', 'cancelado', 'nao_compareceu'] as const;

export const desejoCriarSchema = z
  .object({
    marca: textoOpc(60),
    modelo: textoOpc(80),
    anoMin: z.number().int().min(1950).max(2100).nullable().optional(),
    anoMax: z.number().int().min(1950).max(2100).nullable().optional(),
    precoMaxCentavos: centavos.nullable().optional(),
  })
  .refine((d) => d.marca || d.modelo || d.precoMaxCentavos, {
    message: 'Informe marca, modelo ou preço máximo',
  });
export type DesejoCriar = z.infer<typeof desejoCriarSchema>;

/** O carro atende ao desejo? (marca/modelo por trecho, sem acento nem caixa) */
export function desejoCombina(
  d: {
    marca?: string | null;
    modelo?: string | null;
    anoMin?: number | null;
    anoMax?: number | null;
    precoMaxCentavos?: number | null;
  },
  v: { marca: string; modelo: string; anoModelo: number; precoCentavos: number | null },
): boolean {
  const n = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  if (d.marca && !n(v.marca).includes(n(d.marca))) return false;
  if (d.modelo && !n(v.modelo).includes(n(d.modelo))) return false;
  if (d.anoMin && v.anoModelo < d.anoMin) return false;
  if (d.anoMax && v.anoModelo > d.anoMax) return false;
  if (d.precoMaxCentavos && (!v.precoCentavos || v.precoCentavos > d.precoMaxCentavos))
    return false;
  return true;
}

// ─────────── Central de Atendimento ───────────

export const TIPOS_CANAL = ['whatsapp', 'instagram', 'chat_site'] as const;
export type TipoCanal = (typeof TIPOS_CANAL)[number];

export const mensagemEnviarSchema = z.object({
  texto: texto(4000).min(1, 'Escreva a mensagem'),
});
export const fichaEnviarSchema = z.object({ veiculoId: z.string().uuid() });
export const transferirConversaSchema = z.object({ vendedorId: z.string().uuid() });
export const respostaRapidaSchema = z.object({
  atalho: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9-]{2,30}$/, 'Use letras, números e hífen'),
  texto: texto(2000).min(2),
});
export const canalConectarSchema = z.object({
  tipo: z.enum(['whatsapp', 'instagram']),
  filialId: z.string().uuid(),
  nome: texto(60).min(2),
  identificador: z
    .string()
    .trim()
    .regex(/^\d{5,30}$/, 'Identificador numérico do canal'),
  token: z.string().min(10).max(2000),
});
export const DIAS_SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'] as const;
const hora = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const horarioSchema = z.record(z.enum(DIAS_SEMANA), z.tuple([hora, hora]).nullable());
export type HorarioAtendimento = z.infer<typeof horarioSchema>;
export const HORARIO_PADRAO: HorarioAtendimento = {
  dom: null,
  seg: ['08:00', '18:00'],
  ter: ['08:00', '18:00'],
  qua: ['08:00', '18:00'],
  qui: ['08:00', '18:00'],
  sex: ['08:00', '18:00'],
  sab: ['08:00', '13:00'],
};

/** Está dentro do horário? Usa o fuso de Brasília. */
export function dentroDoHorario(h: HorarioAtendimento | null | undefined, quando: Date): boolean {
  const horario = h ?? HORARIO_PADRAO;
  const partes = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/Sao_Paulo',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(quando);
  const dia = partes
    .find((p) => p.type === 'weekday')!
    .value.toLowerCase()
    .slice(0, 3);
  const mapa: Record<string, (typeof DIAS_SEMANA)[number]> = {
    sun: 'dom',
    mon: 'seg',
    tue: 'ter',
    wed: 'qua',
    thu: 'qui',
    fri: 'sex',
    sat: 'sab',
  };
  const faixa = horario[mapa[dia]!];
  if (!faixa) return false;
  const hh = partes.find((p) => p.type === 'hour')!.value;
  const mm = partes.find((p) => p.type === 'minute')!.value;
  const agora = `${hh === '24' ? '00' : hh}:${mm}`;
  return agora >= faixa[0] && agora < faixa[1];
}

/** WhatsApp: mensagem livre só até 24 h depois da última mensagem do cliente. */
export const JANELA_WHATSAPP_MS = 24 * 3600 * 1000;

// ─────────── Equipe ───────────

export const conviteCriarSchema = z.object({
  nome: texto(120).min(2),
  email: z.string().trim().toLowerCase().email().max(200),
  papel: z.enum(['dono', 'gerente', 'vendedor', 'financeiro']),
  filialIds: z.array(z.string().uuid()).min(1, 'Escolha ao menos uma filial'),
});
export const conviteAceitarSchema = z.object({
  token: z.string().min(20).max(200),
  senha: senhaSchema,
});
export const usuarioAtualizarSchema = z.object({
  papel: z.enum(['dono', 'gerente', 'vendedor', 'financeiro']).optional(),
  filialIds: z.array(z.string().uuid()).min(1).optional(),
  ativo: z.boolean().optional(),
});

export const TIPOS_META = ['vendas', 'faturamento'] as const;
export const metaSalvarSchema = z.object({
  mes: z.string().regex(/^\d{4}-\d{2}$/),
  tipo: z.enum(TIPOS_META),
  valor: z.number().int().min(0).max(100_000_000_00),
  filialId: z.string().uuid().nullable().optional(),
  usuarioId: z.string().uuid().nullable().optional(),
});

export const TIPOS_COMISSAO = [
  'venda_percentual',
  'venda_fixa',
  'lucro_percentual',
  'meta_batida',
  'financiamento',
  'acessorio',
  'garantia',
  'compra_percentual',
  'compra_fixa',
] as const;
export type TipoComissao = (typeof TIPOS_COMISSAO)[number];
export const NOME_TIPO_COMISSAO: Record<TipoComissao, string> = {
  venda_percentual: '% do valor da venda',
  venda_fixa: 'Valor fixo por venda',
  lucro_percentual: '% do lucro da venda',
  meta_batida: 'Bônus por meta batida',
  financiamento: 'Por financiamento fechado',
  acessorio: '% dos acessórios',
  garantia: '% das garantias',
  compra_percentual: '% do valor de compra do carro',
  compra_fixa: 'Valor fixo por carro comprado',
};
/** Tipos em que `valor` é percentual em centésimos (150 = 1,5%). Os demais são centavos. */
export const COMISSAO_PERCENTUAL: TipoComissao[] = [
  'venda_percentual',
  'lucro_percentual',
  'acessorio',
  'garantia',
  'compra_percentual',
];
export const regraComissaoSchema = z.object({
  nome: texto(80).min(2),
  tipo: z.enum(TIPOS_COMISSAO),
  valor: z.number().int().min(0).max(100_000_000),
  filialId: z.string().uuid().nullable().optional(),
  vigenciaInicio: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  vigenciaFim: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
});

export const ADICIONAIS_VENDA = ['acessorio', 'garantia', 'seguro'] as const;
export const adicionalVendaSchema = z.object({
  tipo: z.enum(ADICIONAIS_VENDA),
  descricao: texto(120).min(2),
  valorCentavos: centavos.refine((v) => v > 0),
  /** Fase 4: garantia/seguro vendidos por parceiro habilitado (a loja recebe a comissão) */
  parceiro: textoOpc(60),
  comissaoCentavos: centavos.optional(),
});
export type AdicionalVenda = z.infer<typeof adicionalVendaSchema>;

export interface VendaComissionavel {
  valorCentavos: number;
  custoTotalCentavos: number;
  forma: 'a_vista' | 'financiado' | 'misto';
  financiadoCentavos: number | null;
  adicionais: AdicionalVenda[];
}
export interface RegraCalculo {
  id: string;
  tipo: TipoComissao;
  valor: number;
  nome: string;
}

/** Comissões de UMA venda pelas regras vigentes (meta batida é calculada no fechamento do mês). */
export function calcularComissoesVenda(
  v: VendaComissionavel,
  regras: RegraCalculo[],
): { regraId: string; baseCentavos: number; valorCentavos: number; descricao: string }[] {
  const pct = (base: number, centesimos: number) => Math.round((base * centesimos) / 10_000);
  // Garantia e seguro somam juntos na regra "% das garantias"
  const soma = (t: 'acessorio' | 'garantia') =>
    v.adicionais
      .filter((a) => (t === 'garantia' ? a.tipo !== 'acessorio' : a.tipo === t))
      .reduce((s, a) => s + a.valorCentavos, 0);
  const r: ReturnType<typeof calcularComissoesVenda> = [];
  for (const g of regras) {
    let base = 0;
    let valor = 0;
    switch (g.tipo) {
      case 'venda_percentual':
        base = v.valorCentavos;
        valor = pct(base, g.valor);
        break;
      case 'venda_fixa':
        base = v.valorCentavos;
        valor = g.valor;
        break;
      case 'lucro_percentual':
        base = Math.max(0, v.valorCentavos - v.custoTotalCentavos);
        valor = pct(base, g.valor);
        break;
      case 'financiamento':
        if (v.forma === 'a_vista' || !v.financiadoCentavos) continue;
        base = v.financiadoCentavos;
        valor = g.valor;
        break;
      case 'acessorio':
      case 'garantia':
        base = soma(g.tipo);
        if (!base) continue;
        valor = pct(base, g.valor);
        break;
      default:
        continue;
    }
    if (valor > 0)
      r.push({ regraId: g.id, baseCentavos: base, valorCentavos: valor, descricao: g.nome });
  }
  return r;
}

/** Comissão de quem comprou (avaliou) o carro para a loja, pelas regras de compra vigentes. */
export function calcularComissoesCompra(
  valorCompraCentavos: number,
  regras: RegraCalculo[],
): { regraId: string; baseCentavos: number; valorCentavos: number; descricao: string }[] {
  const r: ReturnType<typeof calcularComissoesCompra> = [];
  for (const g of regras) {
    const valor =
      g.tipo === 'compra_percentual'
        ? Math.round((valorCompraCentavos * g.valor) / 10_000)
        : g.tipo === 'compra_fixa'
          ? g.valor
          : 0;
    if (valor > 0)
      r.push({
        regraId: g.id,
        baseCentavos: valorCompraCentavos,
        valorCentavos: valor,
        descricao: g.nome,
      });
  }
  return r;
}

/** "2026-10" → Date UTC do primeiro dia do mês. */
export const inicioDoMes = (mes: string) => new Date(`${mes}-01T00:00:00.000Z`);
export const mesDe = (d: Date) => d.toISOString().slice(0, 7);

// ─────────── Importador de clientes e leads (mesmo padrão do de veículos) ───────────

export const CAMPOS_CLIENTE = [
  {
    campo: 'nome',
    rotulo: 'Nome',
    obrigatorio: true,
    apelidos: ['nome', 'cliente', 'nome completo', 'contato'],
  },
  {
    campo: 'telefone',
    rotulo: 'Telefone',
    obrigatorio: false,
    apelidos: ['telefone', 'celular', 'whatsapp', 'fone', 'tel'],
  },
  { campo: 'email', rotulo: 'E-mail', obrigatorio: false, apelidos: ['email', 'e-mail', 'e mail'] },
  { campo: 'cpf', rotulo: 'CPF', obrigatorio: false, apelidos: ['cpf', 'documento'] },
  { campo: 'cidade', rotulo: 'Cidade', obrigatorio: false, apelidos: ['cidade', 'municipio'] },
  {
    campo: 'interesse',
    rotulo: 'Carro de interesse',
    obrigatorio: false,
    apelidos: ['interesse', 'carro', 'veiculo de interesse', 'modelo'],
  },
  {
    campo: 'observacoes',
    rotulo: 'Observações',
    obrigatorio: false,
    apelidos: ['observacoes', 'obs', 'anotacoes'],
  },
] as const;
export type CampoCliente = (typeof CAMPOS_CLIENTE)[number]['campo'];
export const camposCliente = CAMPOS_CLIENTE.map((c) => c.campo) as [
  CampoCliente,
  ...CampoCliente[],
];
export const mapeamentoClientesSchema = z
  .record(z.string(), z.enum(camposCliente).nullable())
  .refine((m) => Object.values(m).includes('nome'), { message: 'Mapeie a coluna do nome' })
  .refine((m) => Object.values(m).includes('telefone') || Object.values(m).includes('email'), {
    message: 'Mapeie telefone ou e-mail',
  })
  .refine(
    (m) => {
      const u = Object.values(m).filter(Boolean);
      return new Set(u).size === u.length;
    },
    { message: 'Cada campo só pode vir de uma coluna' },
  );

const normalizarCabecalho = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[_\-.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export function sugerirMapeamentoClientes(
  cabecalhos: string[],
): Record<string, CampoCliente | null> {
  const r: Record<string, CampoCliente | null> = {};
  const usados = new Set<string>();
  for (const cab of cabecalhos) {
    const n = normalizarCabecalho(cab);
    const achado = CAMPOS_CLIENTE.find(
      (c) => !usados.has(c.campo) && c.apelidos.some((a) => normalizarCabecalho(a) === n),
    );
    r[cab] = achado?.campo ?? null;
    if (achado) usados.add(achado.campo);
  }
  return r;
}

export interface ClienteConvertido {
  nome: string;
  telefone: string | null;
  email: string | null;
  cpf: string | null;
  cidade: string | null;
  interesse: string | null;
  observacoes: string | null;
}

export function converterLinhaCliente(
  bruta: Record<string, string>,
  mapa: Record<string, CampoCliente | null>,
): { ok: true; valor: ClienteConvertido } | { ok: false; erros: string[] } {
  const v: Partial<Record<CampoCliente, string>> = {};
  for (const [col, campo] of Object.entries(mapa))
    if (campo) v[campo] = (bruta[col] ?? '').toString().trim();
  const erros: string[] = [];
  const nome = v.nome ?? '';
  if (nome.length < 2) erros.push('Nome vazio');
  const telefone = v.telefone ? normalizarTelefone(v.telefone) : null;
  if (v.telefone && !telefoneValido(v.telefone)) erros.push(`Telefone inválido: "${v.telefone}"`);
  const email = v.email ? v.email.toLowerCase() : null;
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
    erros.push(`E-mail inválido: "${v.email}"`);
  if (!telefone && !email) erros.push('Sem telefone nem e-mail');
  const cpf = v.cpf ? v.cpf.replace(/\D/g, '') : null;
  if (cpf && !cpfValido(cpf)) erros.push('CPF inválido');
  if (erros.length) return { ok: false, erros };
  return {
    ok: true,
    valor: {
      nome: nome.slice(0, 120),
      telefone,
      email,
      cpf,
      cidade: v.cidade || null,
      interesse: v.interesse ? v.interesse.slice(0, 120) : null,
      observacoes: v.observacoes ? v.observacoes.slice(0, 2000) : null,
    },
  };
}

// ─────────── Carteira de clientes (tela Clientes) ───────────

/** Visões em abas da tela Clientes (cada uma com contagem). */
export const VISOES_CLIENTES = [
  'todos',
  'negociando',
  'quentes',
  'compraram',
  'sem_contato',
  'novos',
] as const;
export type VisaoClientes = (typeof VISOES_CLIENTES)[number];
export const NOME_VISAO_CLIENTES: Record<VisaoClientes, string> = {
  todos: 'Todos',
  negociando: 'Em negociação',
  quentes: 'Quentes',
  compraram: 'Compraram',
  sem_contato: 'Sem contato há 30+ dias',
  novos: 'Novos',
};
export const AJUDA_VISAO_CLIENTES: Record<VisaoClientes, string> = {
  todos: 'Todos os clientes da loja.',
  negociando: 'Têm um atendimento aberto no funil.',
  quentes: 'Em negociação com proposta aberta, test drive marcado ou conversa nos últimos 2 dias.',
  compraram: 'Já compraram um carro na loja.',
  sem_contato: 'Ninguém falou com eles há 30 dias ou mais. Bom para reativar.',
  novos: 'Entraram nos últimos 7 dias.',
};

export const SITUACOES_CLIENTE = ['negociando', 'comprou', 'perdido', 'contato'] as const;
export type SituacaoCliente = (typeof SITUACOES_CLIENTE)[number];
export const NOME_SITUACAO_CLIENTE: Record<SituacaoCliente, string> = {
  negociando: 'Em negociação',
  comprou: 'Comprou',
  perdido: 'Não fechou',
  contato: 'Contato',
};

export const ORDENS_CLIENTES = ['recentes', 'nome', 'sem_contato', 'valor'] as const;
export type OrdemClientes = (typeof ORDENS_CLIENTES)[number];
export const NOME_ORDEM_CLIENTES: Record<OrdemClientes, string> = {
  recentes: 'Contato mais recente',
  nome: 'Nome (A–Z)',
  sem_contato: 'Mais tempo sem contato',
  valor: 'Quem mais comprou',
};

export const POR_PAGINA_CLIENTES = 40;

export const clientesFiltroSchema = z.object({
  visao: z.enum(VISOES_CLIENTES).default('todos'),
  busca: z.string().trim().max(60).optional(),
  vendedorId: z.string().uuid().optional(),
  origem: z.string().trim().max(30).optional(),
  cidade: z.string().trim().max(80).optional(),
  ordem: z.enum(ORDENS_CLIENTES).default('recentes'),
  pagina: z.coerce.number().int().min(1).max(1000).default(1),
});
export type ClientesFiltro = z.infer<typeof clientesFiltroSchema>;

export interface SinaisCliente {
  leadsAbertos: number;
  leadsPerdidos: number;
  compras: number;
  propostaAberta: boolean;
  testDriveAgendado: boolean;
  ultimoContato: Date;
  criadoEm: Date;
}

export interface ClassificacaoCliente {
  situacao: SituacaoCliente;
  quente: boolean;
  semContato: boolean;
  novo: boolean;
  jaComprou: boolean;
  diasSemContato: number;
}

/** Situação e sinais do cliente, usados nas visões e nas etiquetas da tela Clientes. */
export function classificarCliente(s: SinaisCliente, agora = new Date()): ClassificacaoCliente {
  const dia = 86_400_000;
  const diasSemContato = Math.max(
    0,
    Math.floor((agora.getTime() - s.ultimoContato.getTime()) / dia),
  );
  const situacao: SituacaoCliente =
    s.leadsAbertos > 0
      ? 'negociando'
      : s.compras > 0
        ? 'comprou'
        : s.leadsPerdidos > 0
          ? 'perdido'
          : 'contato';
  return {
    situacao,
    quente:
      situacao === 'negociando' && (s.propostaAberta || s.testDriveAgendado || diasSemContato <= 2),
    semContato: diasSemContato >= 30,
    novo: agora.getTime() - s.criadoEm.getTime() <= 7 * dia,
    jaComprou: s.compras > 0,
    diasSemContato,
  };
}

/** O cliente entra na visão? */
export function clienteNaVisao(v: VisaoClientes, c: ClassificacaoCliente): boolean {
  switch (v) {
    case 'todos':
      return true;
    case 'negociando':
      return c.situacao === 'negociando';
    case 'quentes':
      return c.quente;
    case 'compraram':
      return c.jaComprou;
    case 'sem_contato':
      return c.semContato;
    case 'novos':
      return c.novo;
  }
}

/**
 * Leads automáticos: a rotina diária cria a oportunidade no funil para o vendedor do cliente.
 * `dias`: antecedência (aniversários e fim do financiamento) ou espera (pós-avaliação e visita).
 */
export const TIPOS_LEAD_AUTOMATICO = [
  'aniversario_cliente',
  'aniversario_compra',
  'pos_avaliacao',
  'fim_financiamento',
  'retorno_visita',
] as const;
export type TipoLeadAutomatico = (typeof TIPOS_LEAD_AUTOMATICO)[number];
export const LEAD_AUTOMATICO: Record<
  TipoLeadAutomatico,
  { nome: string; ajuda: string; diasPadrao: number; rotuloDias: string }
> = {
  aniversario_cliente: {
    nome: 'Aniversário do cliente',
    ajuda: 'Para dar os parabéns (precisa da data de nascimento na ficha).',
    diasPadrao: 0,
    rotuloDias: 'dias antes',
  },
  aniversario_compra: {
    nome: 'Aniversário de compra',
    ajuda: 'Um ano (e a cada ano) depois da compra: hora de oferecer a troca.',
    diasPadrao: 7,
    rotuloDias: 'dias antes',
  },
  pos_avaliacao: {
    nome: 'Avaliou e não fechou',
    ajuda: 'O carro do cliente foi avaliado e o negócio não saiu.',
    diasPadrao: 3,
    rotuloDias: 'dias depois',
  },
  fim_financiamento: {
    nome: 'Fim do financiamento',
    ajuda: 'O financiamento do carro comprado aqui está acabando.',
    diasPadrao: 60,
    rotuloDias: 'dias antes',
  },
  retorno_visita: {
    nome: 'Retorno de visita',
    ajuda: 'Fez test drive e ainda não comprou.',
    diasPadrao: 2,
    rotuloDias: 'dias depois',
  },
};
const regraAutomatica = z.object({
  ativo: z.boolean(),
  dias: z.number().int().min(0).max(365),
});
export const leadsAutomaticosSchema = z.object({
  aniversario_cliente: regraAutomatica,
  aniversario_compra: regraAutomatica,
  pos_avaliacao: regraAutomatica,
  fim_financiamento: regraAutomatica,
  retorno_visita: regraAutomatica,
});
export type LeadsAutomaticos = z.infer<typeof leadsAutomaticosSchema>;
export const LEADS_AUTOMATICOS_PADRAO: LeadsAutomaticos = Object.fromEntries(
  TIPOS_LEAD_AUTOMATICO.map((t) => [t, { ativo: true, dias: LEAD_AUTOMATICO[t].diasPadrao }]),
) as LeadsAutomaticos;

// ─────────── Atendente IA (SDR) ───────────

/** Como a loja usa o atendente IA: em todas as conversas novas ou só fora do horário. */
export const atendenteIaConfigSchema = z.object({
  ativo: z.boolean(),
  quando: z.enum(['sempre', 'fora_do_horario']),
  /** Nome com que a IA se apresenta (ex.: "Ana, assistente virtual") */
  nome: z.string().trim().min(2).max(30),
  /** Depois de tantas respostas, passa para o vendedor mesmo sem terminar */
  maxRespostas: z.number().int().min(2).max(30),
});
export type AtendenteIaConfig = z.infer<typeof atendenteIaConfigSchema>;
export const ATENDENTE_IA_PADRAO: AtendenteIaConfig = {
  ativo: false,
  quando: 'sempre',
  nome: 'Assistente',
  maxRespostas: 8,
};

/** O que a IA descobriu na conversa (vai para o lead e para o vendedor). */
export interface QualificacaoIa {
  interesse: string | null;
  troca: { tem: boolean | null; carro: string | null };
  pagamento: { forma: 'a_vista' | 'financiado' | null; entradaReais: number | null };
  visita: string | null;
  resumo: string | null;
}
export const QUALIFICACAO_VAZIA: QualificacaoIa = {
  interesse: null,
  troca: { tem: null, carro: null },
  pagamento: { forma: null, entradaReais: null },
  visita: null,
  resumo: null,
};

/** Resumo em uma linha para o vendedor (notificação e ficha). */
export function resumoQualificacao(q: QualificacaoIa): string {
  const p: string[] = [];
  if (q.interesse) p.push(`Interesse: ${q.interesse}`);
  if (q.troca.tem === true) p.push(`Troca: ${q.troca.carro ?? 'tem carro'}`);
  if (q.troca.tem === false) p.push('Sem troca');
  if (q.pagamento.forma === 'a_vista') p.push('À vista');
  if (q.pagamento.forma === 'financiado')
    p.push(
      `Financiado${q.pagamento.entradaReais ? `, entrada de R$ ${q.pagamento.entradaReais.toLocaleString('pt-BR')}` : ''}`,
    );
  if (q.visita) p.push(`Visita: ${q.visita}`);
  return p.join(' · ') || 'Ainda sem detalhes';
}
